import { useCallback, useEffect, useState } from "react";
import { Car, Users, Sparkle, ShieldCheck, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Parcels } from "@/components/Parcels";
import { Parking } from "@/components/Parking";
import { useWhere, GpsBar, Planner, RideJourney, CompanionPost } from "@/components/RideExtras";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export default function Companion() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    leg: "home-office", from_place: "", to_place: "", when: "07:30 tomorrow", back_by: "",
    seats: 3, driving: true, vehicle: "my own car", company: "either", miles: 12, note: "",
    parking: "none", parking_bay: "", van_booking_id: "", public: true,
    drop_anywhere: true, return_to: "none", stops: "",
  });
  const [share, setShare] = useState([]);
  const [cost, setCost] = useState(null);
  const [matches, setMatches] = useState(null);
  const [nearby, setNearby] = useState(null);
  const { where, take, busy: locating } = useWhere();

  const load = useCallback(() => {
    api.get("/rides").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    api.post("/rides/cost", { miles: Number(form.miles) || 0, riders: Math.max(1, Number(form.seats) || 1) })
      .then(({ data: d }) => setCost(d)).catch(() => setCost(null));
  }, [data, form.miles, form.seats]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the rides…</p>;

  const sym = cost?.symbol || data.zone?.symbol || "$";
  const needsNod = data.approval_legs.includes(form.leg);

  return (
    <div className="space-y-6" data-testid="companion-page">
      <div>
        <p className="overline text-[#B3241B]">Companion rides</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Car size={30} weight="duotone" className="text-[#002FA7]" /> Nobody travels alone
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="companion-explainer">
          A lift and a bit of company to the office, out to a work site, on a lone survey and back
          home again — passed round your own colleagues, crew and boss. Split the mileage, and tap
          landed safe at the other end.
        </p>
      </div>

      <GpsBar where={where} onTake={take} busy={locating}
        onFrom={(w) => setForm({ ...form, from_place: w.name || form.from_place, from_lat: w.lat, from_lng: w.lng })}
        onTo={(w) => setForm({ ...form, to_place: w.name || form.to_place, to_lat: w.lat, to_lng: w.lng })}
        onNear={async (w) => {
          try {
            const { data: d } = await api.get(`/rides/near?lat=${w.lat}&lng=${w.lng}`);
            setNearby(d);
            toast.success(d.near.length ? `${d.near.length} runs near you` : "Nothing pinned near you yet");
          } catch (e) { oops(e); }
        }} />

      {nearby && (
        <div className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="companion-near">
          <p className="text-sm text-[#0F172A]">Runs nearest you{nearby.you_are ? ` — ${nearby.you_are}` : ""}</p>
          <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
            {nearby.near.slice(0, 6).map((r) => (
              <li key={r.id} data-testid={`companion-near-${r.id}`}>
                {r.miles_away} miles away · {r.owner_name} · {r.from_place} → {r.to_place} · {r.when}
              </li>
            ))}
            {nearby.near.length === 0 && <li>No runs with a pin on the map yet.</li>}
          </ul>
        </div>
      )}

      <Planner where={where} onApply={(p) => setForm({
        ...form, leg: p.leg || form.leg, from_place: p.from_place || form.from_place,
        to_place: p.to_place || form.to_place, when: p.when || form.when,
        back_by: p.back_by || form.back_by, seats: p.seats ?? form.seats,
        vehicle: (data.vehicles || []).includes(p.vehicle) ? p.vehicle : form.vehicle,
        company: p.company || form.company, parking: p.parking || form.parking,
        return_to: p.return_to || form.return_to, miles: p.miles ?? form.miles,
        stops: (p.stops || []).join(", "),
      })} />

      <div className="border border-border bg-white p-4" data-testid="companion-post-box">
        <p className="overline text-slate-500">Post a run</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <select data-testid="companion-leg" value={form.leg} aria-label="Which leg"
            onChange={(e) => setForm({ ...form, leg: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.legs.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
          </select>
          <select data-testid="companion-vehicle" value={form.vehicle} aria-label="What are you in"
            onChange={(e) => setForm({ ...form, vehicle: e.target.value, driving: e.target.value !== "looking for a lift" })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.vehicles.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <Input data-testid="companion-from" placeholder="From — home, depot, yard"
            value={form.from_place} onChange={(e) => setForm({ ...form, from_place: e.target.value })} />
          <Input data-testid="companion-to" placeholder="To — office, site, survey"
            value={form.to_place} onChange={(e) => setForm({ ...form, to_place: e.target.value })} />
          <Input data-testid="companion-when" placeholder="Setting off — 07:30 tomorrow"
            value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} />
          <Input data-testid="companion-back" placeholder="Back by (optional)"
            value={form.back_by} onChange={(e) => setForm({ ...form, back_by: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Seats free
            <Input data-testid="companion-seats" type="number" min={0} max={16} value={form.seats}
              onChange={(e) => setForm({ ...form, seats: e.target.value })} className="w-20" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Miles
            <Input data-testid="companion-miles" type="number" min={0} value={form.miles}
              onChange={(e) => setForm({ ...form, miles: e.target.value })} className="w-24" />
          </label>
          <select data-testid="companion-company" value={form.company} aria-label="Company"
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm sm:col-span-2">
            {data.company_options.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
          <select data-testid="companion-return" value={form.return_to} aria-label="Coming back"
            onChange={(e) => setForm({ ...form, return_to: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {(data.returns || []).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
          <Input data-testid="companion-stops" placeholder="Stops along the way, comma separated"
            value={form.stops} onChange={(e) => setForm({ ...form, stops: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
            <input type="checkbox" data-testid="companion-drop-anywhere" checked={form.drop_anywhere}
              onChange={(e) => setForm({ ...form, drop_anywhere: e.target.checked })} />
            I can drop you anywhere along the way — home, office or wherever suits
          </label>
          <select data-testid="companion-parking" value={form.parking} aria-label="Parking"
            onChange={(e) => setForm({ ...form, parking: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.parking_options.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <Input data-testid="companion-bay" placeholder="Bay or permit (optional)"
            value={form.parking_bay} onChange={(e) => setForm({ ...form, parking_bay: e.target.value })} />
          {(data.my_vans || []).length > 0 && (
            <select data-testid="companion-van" value={form.van_booking_id} aria-label="Hire van"
              onChange={(e) => setForm({ ...form, van_booking_id: e.target.value, vehicle: e.target.value ? "hire van" : form.vehicle })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm sm:col-span-2">
              <option value="">Not using a hire van</option>
              {data.my_vans.map((v) => (
                <option key={v.id} value={v.id}>{v.vehicle} · {v.reference} · fill the seats</option>
              ))}
            </select>
          )}
        </div>

        <p className="mt-3 border-l-2 border-[#0B5C2E] bg-[#F3FAF5] px-3 py-2 text-sm text-slate-700"
          data-testid="companion-parking-note">
          Every seat filled is one less car hunting for a space — at the works gate and on your own
          street. Hand your bay over to whoever is driving in, or share one between you.
        </p>

        <div className="mt-3">
          <p className="overline text-slate-500">
            <PaperPlaneTilt size={13} className="mr-1 inline align-[-2px]" /> Tell your folks
          </p>
          <div className="mt-1 flex flex-wrap gap-2" data-testid="companion-mates">
            {data.mates.slice(0, 12).map((m) => {
              const on = share.includes(m.id);
              return (
                <button key={m.id} type="button" data-testid={`companion-mate-${m.id}`}
                  onClick={() => setShare(on ? share.filter((i) => i !== m.id) : [...share, m.id])}
                  className={`tap-safe border px-3 py-2 text-xs font-semibold ${
                    on ? "border-[#002FA7] bg-[#002FA7] text-white"
                      : "border-border bg-white text-slate-700 hover:border-[#002FA7]"}`}>
                  {m.name}{m.role === "admin" || m.role === "manager" ? " · boss" : ""}
                </button>
              );
            })}
          </div>
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" data-testid="companion-public" checked={form.public}
            onChange={(e) => setForm({ ...form, public: e.target.checked })} />
          Show it on the public rides board too
        </label>

        {cost && (
          <p className="mt-3 text-sm text-slate-700" data-testid="companion-cost">
            Mileage works out at {sym}{Number(cost.run_cost).toFixed(2)} for the run —{" "}
            <span className="font-semibold text-[#0F172A]">
              {sym}{Number(cost.per_head).toFixed(2)} each
            </span>{" "}
            with {cost.riders} on board.
          </p>
        )}
        {needsNod && (
          <p className="mt-1 text-xs text-[#B3241B]" data-testid="companion-nod-note">
            Site and survey runs go to a manager for the nod — they will get a notification.
          </p>
        )}

        <Button className="mt-3" size="sm" data-testid="companion-post" disabled={busy}
          onClick={() => act(async () => {
            const { data: d } = await api.post("/rides", {
              ...form, seats: Number(form.seats) || 0, miles: Number(form.miles) || 0,
              stops: form.stops.split(",").map((s) => s.trim()).filter(Boolean),
              share_with: share,
            });
            setForm({ ...form, from_place: "", to_place: "", note: "", stops: "" });
            setShare([]);
            return d;
          }, "Run posted — your lot have been told")}>
          Post the run
        </Button>
      </div>

      <div className="border border-border bg-white p-4" data-testid="companion-match-box">
        <p className="overline text-slate-500">
          <Sparkle size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Who is going my way?
        </p>
        <Button className="mt-2" size="sm" variant="outline" data-testid="companion-match"
          disabled={busy || form.from_place.trim().length < 2 || form.to_place.trim().length < 2}
          onClick={() => act(async () => {
            const { data: d } = await api.post("/rides/match", {
              from_place: form.from_place, to_place: form.to_place, when: form.when });
            setMatches(d);
          }, "Had a look round")}>
          Find me a lift
        </Button>
        {matches && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="companion-matches">
            <p className="text-sm text-[#0F172A]">{matches.line}</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {matches.matches.map((m) => (
                <li key={m.ride_id} data-testid={`companion-match-${m.ride_id}`}>
                  <span className="font-semibold text-[#0F172A]">{m.fit}</span> · {m.why}
                  {m.detour_miles ? ` · ${m.detour_miles} miles out of their way` : ""}
                </li>
              ))}
              {matches.matches.length === 0 && <li>Nothing lines up just now.</li>}
            </ul>
          </div>
        )}
      </div>

      <div data-testid="companion-rides">
        <p className="overline text-slate-500">
          <Users size={13} className="mr-1 inline align-[-2px]" /> Runs posted
        </p>
        <ul className="mt-2 space-y-2">
          {data.rides.map((r) => {
            const seatsLeft = Math.max(0, (r.seats || 0) - (r.riders || []).length);
            const onBoard = (r.riders || []).some((x) => x.user_id === data.me.id);
            const isMine = r.owner_id === data.me.id;
            return (
              <li key={r.id} className="border border-border bg-white p-3" data-testid={`companion-ride-${r.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">
                  {r.leg_name} · {r.from_place} → {r.to_place}
                </p>
                <p className="text-xs text-slate-600">
                  {r.owner_name} · {r.when}{r.back_by ? ` · back by ${r.back_by}` : ""} ·
                  {" "}{r.vehicle} · {seatsLeft} seat(s) left · {r.miles} miles
                  {r.company === "rather-not-alone" ? " · would rather not travel alone" : ""}
                  {r.parking === "hand-over" ? " · bay going spare" : ""}
                  {r.parking === "need-bay" ? " · needs a bay" : ""}
                  {r.parking === "share-bay" ? " · sharing one bay" : ""}
                  {r.van_label ? ` · ${r.van_label} on hire` : ""}
                  {r.drop_anywhere ? " · drops you anywhere on the way" : ""}
                  {r.return_to && r.return_to !== "none" ? ` · ${r.return_name?.toLowerCase()}` : ""}
                  {(r.stops || []).length ? ` · via ${r.stops.join(", ")}` : ""}
                </p>
                <p className="mt-1 text-xs">
                  {r.landed ? (
                    <span className="font-semibold text-[#0B5C2E]">Landed safe</span>
                  ) : r.needs_approval && !r.approved ? (
                    <span className="font-semibold text-[#B3241B]">Waiting on the manager</span>
                  ) : (
                    <span className="text-slate-500">{r.status}{r.approved_by ? ` · nodded through by ${r.approved_by}` : ""}</span>
                  )}
                  {(r.riders || []).length > 0 && (
                    <span className="text-slate-500"> · riding: {(r.riders || []).map((x) => x.name).join(", ")}</span>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {!isMine && !onBoard && seatsLeft > 0 && r.status === "open" && (
                    <Button size="sm" variant="outline" data-testid={`companion-claim-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/claim`, {
                        pickup: form.from_place, dropoff: form.to_place,
                      }), "You are on board")}>
                      Take a seat
                    </Button>
                  )}
                  {onBoard && (
                    <Button size="sm" variant="outline" data-testid={`companion-leave-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/leave`), "Dropped off")}>
                      Drop off
                    </Button>
                  )}
                  {(isMine || onBoard) && !r.landed && (
                    <Button size="sm" data-testid={`companion-landed-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/landed`), "Everyone knows you are there")}>
                      <ShieldCheck size={14} className="mr-1.5" /> Landed safe
                    </Button>
                  )}
                  {isMine && share.length > 0 && (
                    <Button size="sm" variant="outline" data-testid={`companion-share-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/share`, { user_ids: share }), "Passed it on")}>
                      Pass it to the ones I picked
                    </Button>
                  )}
                  {data.can_approve && r.needs_approval && !r.approved && (
                    <Button size="sm" data-testid={`companion-approve-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/approve`), "Nodded through")}>
                      <CheckCircle size={14} className="mr-1.5" /> Approve the site run
                    </Button>
                  )}
                  {isMine && r.status === "open" && (
                    <Button size="sm" variant="outline" data-testid={`companion-cancel-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/${r.id}/cancel`), "Called off")}>
                      Call it off
                    </Button>
                  )}
                  {r.from_lat != null && <RideJourney rideId={r.id} />}
                  {isMine && r.status === "open" && <CompanionPost rideId={r.id} />}
                </div>
              </li>
            );
          })}
          {data.rides.length === 0 && <li className="text-sm text-slate-500">No runs posted yet.</li>}
        </ul>
      </div>

      <Parcels rides={data.rides} mates={data.mates} me={data.me} onChange={load} />
      <Parking where={where} />
    </div>
  );
}
